import React, { useState } from "react";
import classNames from "classnames";
import { LeftArrowIcon, RightArrowIcon } from "@icons";
import { Button } from "@design-system/Button/Button";
import type { DateOnlyRange } from "./DatePicker.types";
import { DEFAULT_INPUT_FORMATS, MONTH_LABELS } from "./DatePicker.lib";
import { MonthRangeAdjacentYearButtons } from "./MonthRangeAdjacentYearButtons";
import { MonthRangeDayInput } from "./MonthRangeDayInput";
import styles from "./DatePicker.module.scss";

interface DatePickerMonthRangeCalendarProps {
    value?: DateOnlyRange;
    onValueChange: (value: DateOnlyRange | undefined) => void;
    minDate?: string;
    maxDate?: string;
    inputFormat?: string | readonly string[];
    className?: string;
    showAdjacentYearButtons?: boolean;
}

/**
 * The grid works on plain year/month numbers derived from the ISO date
 * strings, so no Date construction or formatting can shift a month across
 * timezone or DST boundaries.
 */
const getMonthKey = (isoDate: string): number => {
    const year = Number(isoDate.slice(0, 4));
    const monthIndex = Number(isoDate.slice(5, 7)) - 1;
    return year * 12 + monthIndex;
};

const getFirstDayOfMonth = (monthKey: number): string => {
    const year = Math.floor(monthKey / 12);
    const monthIndex = monthKey % 12;
    return `${year}-${String(monthIndex + 1).padStart(2, "0")}-01`;
};

const getLastDayOfMonth = (monthKey: number): string => {
    const year = Math.floor(monthKey / 12);
    const monthIndex = monthKey % 12;
    const daysInMonth = new Date(
        Date.UTC(year, monthIndex + 1, 0),
    ).getUTCDate();
    return `${year}-${String(monthIndex + 1).padStart(2, "0")}-${String(daysInMonth).padStart(2, "0")}`;
};

const getNextRange = (
    value: DateOnlyRange | undefined,
    monthKey: number,
): DateOnlyRange => {
    const hasOpenRange = Boolean(value?.from && !value.to);

    if (!hasOpenRange || !value?.from) {
        return { from: getFirstDayOfMonth(monthKey), to: undefined };
    }

    const fromMonthKey = getMonthKey(value.from);
    if (monthKey < fromMonthKey) {
        return {
            from: getFirstDayOfMonth(monthKey),
            to: getLastDayOfMonth(fromMonthKey),
        };
    }
    return {
        from: value.from,
        to: getLastDayOfMonth(monthKey),
    };
};

const isDateWithinBounds = (
    isoDate: string,
    minDate?: string,
    maxDate?: string,
): boolean => {
    if (minDate && isoDate < minDate) {
        return false;
    }
    if (maxDate && isoDate > maxDate) {
        return false;
    }
    return true;
};

const MonthRangeYearGrid = ({
    year,
    value,
    minMonthKey,
    maxMonthKey,
    onSelect,
}: {
    year: number;
    value?: DateOnlyRange;
    minMonthKey?: number;
    maxMonthKey?: number;
    onSelect: (monthKey: number) => void;
}) => {
    const fromMonthKey = value?.from ? getMonthKey(value.from) : undefined;
    const toMonthKey = value?.to ? getMonthKey(value.to) : undefined;

    return (
        <div className={styles.monthRangeGrid}>
            {MONTH_LABELS.map((monthLabel, monthIndex) => {
                const monthKey = year * 12 + monthIndex;
                const isStart = monthKey === fromMonthKey;
                const isEnd = monthKey === toMonthKey;
                const isMiddle = Boolean(
                    fromMonthKey !== undefined &&
                    toMonthKey !== undefined &&
                    monthKey > fromMonthKey &&
                    monthKey < toMonthKey,
                );
                const isDisabled = Boolean(
                    (minMonthKey !== undefined && monthKey < minMonthKey) ||
                    (maxMonthKey !== undefined && monthKey > maxMonthKey),
                );

                return (
                    <button
                        key={monthLabel}
                        type="button"
                        className={styles.monthRangeCell}
                        data-range-start={isStart || undefined}
                        data-range-middle={isMiddle || undefined}
                        data-range-end={isEnd || undefined}
                        disabled={isDisabled}
                        onClick={() => onSelect(monthKey)}
                    >
                        {monthLabel}
                    </button>
                );
            })}
        </div>
    );
};

export const DatePickerMonthRangeCalendar = ({
    value,
    onValueChange,
    minDate,
    maxDate,
    inputFormat = DEFAULT_INPUT_FORMATS,
    className,
    showAdjacentYearButtons = false,
}: DatePickerMonthRangeCalendarProps) => {
    const selectedFromYear = value?.from
        ? Math.floor(getMonthKey(value.from) / 12)
        : undefined;
    const [yearNavigation, setYearNavigation] = useState(() => ({
        selectedFromYear,
        startYear: selectedFromYear ?? new Date().getFullYear(),
    }));
    const startYear =
        selectedFromYear === undefined ||
        yearNavigation.selectedFromYear === selectedFromYear
            ? yearNavigation.startYear
            : selectedFromYear;
    const setStartYear = (nextStartYear: number) =>
        setYearNavigation({ selectedFromYear, startYear: nextStartYear });
    const visibleYears = [startYear, startYear + 1];
    const minMonthKey = minDate ? getMonthKey(minDate) : undefined;
    const maxMonthKey = maxDate ? getMonthKey(maxDate) : undefined;
    const canGoPrevious =
        minMonthKey === undefined || startYear > Math.floor(minMonthKey / 12);
    const canGoNext =
        maxMonthKey === undefined ||
        startYear < Math.floor(maxMonthKey / 12) - 1;

    const commitFromDate = (nextFrom: string | undefined) => {
        if (nextFrom === undefined) {
            onValueChange(undefined);
            return;
        }
        if (!isDateWithinBounds(nextFrom, minDate, maxDate)) {
            return;
        }
        if (value?.to && nextFrom > value.to) {
            onValueChange({ from: value.to, to: nextFrom });
            return;
        }
        onValueChange({ from: nextFrom, to: value?.to });
    };

    const commitToDate = (nextTo: string | undefined) => {
        if (nextTo === undefined) {
            if (!value?.from) {
                onValueChange(undefined);
                return;
            }
            onValueChange({ from: value.from, to: undefined });
            return;
        }
        if (!isDateWithinBounds(nextTo, minDate, maxDate)) {
            return;
        }
        if (!value?.from) {
            onValueChange({ from: nextTo, to: undefined });
            return;
        }
        if (nextTo < value.from) {
            onValueChange({ from: nextTo, to: value.from });
            return;
        }
        onValueChange({ from: value.from, to: nextTo });
    };

    return (
        <div className={classNames(styles.monthRangeCalendar, className)}>
            <div className={styles.monthRangeInputs}>
                <MonthRangeDayInput
                    id="month-range-from"
                    label="From"
                    value={value?.from}
                    inputFormat={inputFormat}
                    onCommit={commitFromDate}
                />
                <MonthRangeDayInput
                    id="month-range-to"
                    label="To"
                    value={value?.to}
                    inputFormat={inputFormat}
                    onCommit={commitToDate}
                />
            </div>
            <div className={styles.monthRangeHeader}>
                <Button
                    type="button"
                    iconOnly
                    variant="secondary"
                    className={styles.dateNavigationArrow}
                    disabled={!canGoPrevious}
                    onClick={() => setStartYear(startYear - 1)}
                    aria-label="Previous year"
                >
                    <LeftArrowIcon />
                </Button>
                {visibleYears.map((year) => (
                    <div key={year} className={styles.monthRangeYearLabel}>
                        {year}
                    </div>
                ))}
                <Button
                    type="button"
                    iconOnly
                    variant="secondary"
                    className={styles.dateNavigationArrow}
                    disabled={!canGoNext}
                    onClick={() => setStartYear(startYear + 1)}
                    aria-label="Next year"
                >
                    <RightArrowIcon />
                </Button>
            </div>
            <div className={styles.monthRangeGrids}>
                {visibleYears.map((year) => (
                    <MonthRangeYearGrid
                        key={year}
                        year={year}
                        value={value}
                        minMonthKey={minMonthKey}
                        maxMonthKey={maxMonthKey}
                        onSelect={(monthKey) =>
                            onValueChange(getNextRange(value, monthKey))
                        }
                    />
                ))}
            </div>
            {showAdjacentYearButtons && (
                <MonthRangeAdjacentYearButtons
                    value={value}
                    minDate={minDate}
                    maxDate={maxDate}
                    onValueChange={onValueChange}
                />
            )}
        </div>
    );
};
